import { FC, MouseEvent, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { Box } from '@mui/material';
import { isEmpty } from 'lodash';
import { useSearchParams } from 'react-router-dom';
import { SYSTEM_COLLAPSE } from 'common/actions/row-groups/types';
import { firmDailyNavSettingsUrl } from 'common/config/urls';
import { selectDailyNavApplyDates, selectDailyNavDayConfig } from 'common/constants/dailyNav';
import { useFirmScopedPermissions } from 'common/hooks';
import { rowsGroupsReducer } from 'common/reducers/rowsGroups';
import { useStore } from 'common/store';
import { Row } from 'common/types/scalarSpreadsheet';
import { UseStoreValues } from 'common/types/store';
import { CustomScalarSpreadsheet, MessageBox } from 'components';
import { GridSkeleton } from 'components/Grid';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import { PrimaryNavigationMenu, PrimaryNavigationMenuItem } from 'components/ui';
import type { TertiaryNavigationActionControlConfig } from 'context/FirmBaseLayoutContext';
import { useTertiaryNavigationRegistration } from 'context/FirmBaseLayoutContext';
import { useGetDailyNavConfigApplyPreview } from 'services/hooks/dailyNav';
import {
  useGenerateDailyNavRecords,
  useGetCompanyListByFirm,
  useGetDailyNavRecords,
} from 'services/hooks/firms';
import { getArrayValue, getBooleanValue, getObjectValue } from 'utilities';
import ClosedTransactionsDialog from './components/ClosedTransactionsDialog';
import DayConfigBanner from './components/DayConfigBanner';
import DeltaBreakdownDialog from './components/DeltaBreakdownDialog';
import FirmRulesIcon from './components/FirmRulesIcon';
import MarketDataFreshness from './components/MarketDataFreshness';
import NewsDialog from './components/NewsDialog';
import ReportDateSelector from './components/ReportDateSelector';
import ReportDialog from './components/ReportDialog';
import ReviewPriceDialog from './components/ReviewPriceDialog';
import SecondaryBreakdownDialog from './components/SecondaryBreakdownDialog';
import { DailyNavRecordWithDay, FirmDailyNavDay } from './daily-nav.api.types';
import {
  DAILY_NAV_REPORT_DATE_PARAM,
  DAILY_NAV_REPORT_PHASES,
  DailyNavReportTab,
} from './daily-nav.constants';
import { DailyNavTableRow, useDailyNavSheet } from './hooks/useDailyNavSheet';

const DailyNav: FC = () => {
  const [storeValue] = useStore() as UseStoreValues;
  const { firmInfo, user } = getObjectValue(storeValue);
  const { id: firmId, slug: firmSlug } = getObjectValue(firmInfo);
  const isStaffOrSuperuser = getBooleanValue(user?.is_staff) || getBooleanValue(user?.is_superuser);
  const { canMutateFirmScopedEntities } = useFirmScopedPermissions({ firmId });
  const canReviewPrice = canMutateFirmScopedEntities || isStaffOrSuperuser;

  const [reviewRow, setReviewRow] = useState<DailyNavTableRow | null>(null);
  const [breakdownRow, setBreakdownRow] = useState<DailyNavTableRow | null>(null);
  const [secondaryBreakdownRow, setSecondaryBreakdownRow] = useState<DailyNavTableRow | null>(null);
  const [closedTransactionsRow, setClosedTransactionsRow] = useState<DailyNavTableRow | null>(null);
  const [newsRow, setNewsRow] = useState<DailyNavTableRow | null>(null);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [reportTab, setReportTab] = useState<DailyNavReportTab | undefined>(undefined);
  const [reportMenuAnchorEl, setReportMenuAnchorEl] = useState<HTMLElement | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDate = searchParams.get(DAILY_NAV_REPORT_DATE_PARAM) ?? undefined;

  const setSelectedDate = useCallback(
    (date: string) => {
      setSearchParams(
        previous => {
          const next = new URLSearchParams(previous);
          next.set(DAILY_NAV_REPORT_DATE_PARAM, date);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const { mutate: generateDailyNavRecords, isPending: isGenerating } = useGenerateDailyNavRecords(firmId);
  const { data: companies } = useGetCompanyListByFirm({ firmId });
  const {
    data: dailyNavRecords,
    isFetching: isDailyNavRecordsFetching,
    isLoading: isDailyNavRecordsLoading,
    refetch: refetchDailyNavRecords,
  } = useGetDailyNavRecords({
    firmId,
    date: selectedDate,
  });

  const resolvedDate = dailyNavRecords?.[0]?.date;
  const reportDate = selectedDate ?? resolvedDate;


  const { data: applyPreview } = useGetDailyNavConfigApplyPreview(firmId);
  const isOpenNavDay = useMemo(
    () =>
      Boolean(
        reportDate &&
          selectDailyNavApplyDates(applyPreview?.dates).applicableDates.some(date => date.nav_date === reportDate)
      ),
    [applyPreview?.dates, reportDate]
  );
  const { config: dayConfig, isStale, unanchoredCount } = useMemo(
    () => selectDailyNavDayConfig(dailyNavRecords, isOpenNavDay),
    [dailyNavRecords, isOpenNavDay]
  );

  const day = useMemo<FirmDailyNavDay | null>(() => {
    const fromRecord = (dailyNavRecords ?? [])
      .map(record => (record as DailyNavRecordWithDay).day)
      .find(candidate => candidate != null);
    return fromRecord ?? null;
  }, [dailyNavRecords]);
  const isDayFinalized = Boolean(day?.is_finalized);
  const isDaySent = Boolean(day?.is_sent);
  const marketDataAsOf = day?.market_data_as_of ?? (isDayFinalized ? day?.finalized_at : undefined);
  const showMarketDataFreshness = !isDailyNavRecordsLoading && !isEmpty(dailyNavRecords);

  const handleRefreshMarketData = useCallback(async () => {
    await Promise.all([refetchDailyNavRecords()]);
  }, [refetchDailyNavRecords]);

  const leftActionsNode = useMemo(
    () => (
      <Box display="flex" alignItems="center" gap={1}>
        <ReportDateSelector value={reportDate} onChange={setSelectedDate} />
        <FirmRulesIcon config={dayConfig} settingsUrl={firmSlug ? firmDailyNavSettingsUrl(firmSlug) : undefined} />
        {showMarketDataFreshness && (
          <MarketDataFreshness
            asOf={marketDataAsOf}
            isFinalized={isDayFinalized}
            isRefreshing={isDailyNavRecordsFetching}
            onRefresh={handleRefreshMarketData}
          />
        )}
      </Box>
    ),
    [
      dayConfig,
      firmSlug,
      handleRefreshMarketData,
      isDailyNavRecordsFetching,
      isDayFinalized,
      marketDataAsOf,
      reportDate,
      setSelectedDate,
      showMarketDataFreshness,
    ]
  );

  const canGenerateRecords = isStaffOrSuperuser && !isDailyNavRecordsLoading && isEmpty(dailyNavRecords);

  let reportButtonLabel = 'Report';
  if (isDaySent) {
    reportButtonLabel = 'Sent';
  } else if (isDayFinalized) {
    reportButtonLabel = 'Finalized';
  }
  const isReportDisabled = !firmId || isEmpty(dailyNavRecords);

  const openReport = useCallback((tab?: DailyNavReportTab) => {
    setReportTab(tab);
    setIsReportOpen(true);
    setReportMenuAnchorEl(null);
  }, []);

  const handleReportMenuClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setReportMenuAnchorEl(event.currentTarget);
  }, []);

  const tertiaryNavActions = useMemo<TertiaryNavigationActionControlConfig[]>(() => {
    const actions: TertiaryNavigationActionControlConfig[] = [];
    if (canReviewPrice || isDayFinalized || isDaySent) {
      actions.push({
        type: 'splitButton',
        label: reportButtonLabel,
        variant: 'primary',
        disabled: isReportDisabled,
        testId: 'finalize-daily-nav-button',
        menuAriaLabel: 'Open report phases',
        menuDisabled: isReportDisabled,
        onClick: () => openReport(),
        onMenuClick: handleReportMenuClick,
      });
    }
    if (canGenerateRecords) {
      actions.push({
        type: 'button',
        label: 'Generate Daily NAVs',
        variant: 'primary',
        disabled: isGenerating || !firmId,
        testId: 'generate-daily-nav-records-button',
        onClick: () => generateDailyNavRecords(selectedDate),
      });
    }
    return actions;
  }, [
    canGenerateRecords,
    canReviewPrice,
    firmId,
    generateDailyNavRecords,
    handleReportMenuClick,
    isDayFinalized,
    isDaySent,
    isGenerating,
    isReportDisabled,
    openReport,
    reportButtonLabel,
    selectedDate,
  ]);

  const tertiaryNavigation = useMemo(
    () => ({
      ariaLabel: 'Daily NAV controls',
      actions: tertiaryNavActions,
      leftActionsNode,
    }),
    [leftActionsNode, tertiaryNavActions]
  );

  useTertiaryNavigationRegistration(tertiaryNavigation);

  const { spreadsheet, columnTypes, columnWidths } = useDailyNavSheet({
    companies,
    firmSlug,
    records: dailyNavRecords,
    onReviewClick: setReviewRow,
    canReview: canReviewPrice,
    onBreakdownClick: setBreakdownRow,
    onSecondaryBreakdownClick: setSecondaryBreakdownRow,
    onClosedTransactionsClick: setClosedTransactionsRow,
    onNewsClick: setNewsRow,
  });

  const [rowGroups, setRowGroups] = useReducer(rowsGroupsReducer, {});
  const parentRows = useMemo(
    () => (spreadsheet?.rowConfig ?? []).filter(row => row.hasSubRows) as Row<object>[],
    [spreadsheet]
  );

  useEffect(() => {
    parentRows.forEach(row => {
      if (row.alias && !rowGroups[row.alias]) {
        setRowGroups({ type: SYSTEM_COLLAPSE, row: row.alias });
      }
    });
  }, [parentRows, rowGroups]);

  const spreadsheets = useMemo(() => (spreadsheet?.rowConfig?.length ? [spreadsheet] : []), [spreadsheet]);
  const { onChange, workbook, workbookContextValue } = useWorkbook(spreadsheets);

  const sheetProps = useMemo(() => {
    if (!spreadsheet) {
      return null;
    }
    return {
      ...spreadsheet,
      data: getArrayValue(spreadsheet.data),
      onChange,
      sheet: spreadsheet,
      workbook,
      rowGroups,
      setRowGroups,
      // Without these every column takes the shell's 175px default, and the browser
      // shrinks each one by its own content to fit -- which is what made them ragged.
      columnTypes,
      columnWidths,
    } as unknown as typeof ScalarSpreadsheet;
  }, [spreadsheet, onChange, workbook, rowGroups, columnTypes, columnWidths]);

  return (
    <Box className="daily-nav-container">
      {!isDailyNavRecordsLoading && (
        <DayConfigBanner
          config={dayConfig}
          isStale={isStale}
          unanchoredCount={unanchoredCount}
          firmSettingsUrl={firmSlug ? firmDailyNavSettingsUrl(firmSlug) : undefined}
        />
      )}

      {isDailyNavRecordsLoading && <GridSkeleton maxColumns={10} maxRows={4} />}

      {!isDailyNavRecordsLoading && isEmpty(dailyNavRecords) && (
        <MessageBox
          fullWidth={false}
          title="Daily NAV"
          tagline={
            isStaffOrSuperuser
              ? 'No Daily NAV records yet for this date. Records are generated automatically each trading day; use Generate Daily NAVs to create them now.'
              : 'No Daily NAV records yet for this date. Records are generated automatically each trading day.'
          }
        />
      )}

      {sheetProps && (
        <WorkbookContext.Provider value={workbookContextValue}>
          <CustomScalarSpreadsheet {...sheetProps} isLoading={isDailyNavRecordsLoading} />
        </WorkbookContext.Provider>
      )}

      <PrimaryNavigationMenu
        anchorEl={reportMenuAnchorEl}
        menuMinWidth={180}
        menuWidth="content"
        onClose={() => setReportMenuAnchorEl(null)}
        open={Boolean(reportMenuAnchorEl)}
        data-testid="daily-nav-report-phase-menu">
        {DAILY_NAV_REPORT_PHASES.map(phase => (
          <PrimaryNavigationMenuItem
            key={phase.tab}
            data-testid={`daily-nav-report-menu-${phase.tab}`}
            onClick={() => openReport(phase.tab)}>
            {phase.label}
          </PrimaryNavigationMenuItem>
        ))}
      </PrimaryNavigationMenu>
      <ReportDialog
        open={isReportOpen}
        onClose={() => setIsReportOpen(false)}
        firmId={firmId}
        date={reportDate}
        day={day}
        initialTab={reportTab}
        isStaff={isStaffOrSuperuser}
      />
      <ReviewPriceDialog row={reviewRow} open={Boolean(reviewRow)} onClose={() => setReviewRow(null)} />
      <DeltaBreakdownDialog row={breakdownRow} open={Boolean(breakdownRow)} onClose={() => setBreakdownRow(null)} />
      <SecondaryBreakdownDialog
        row={secondaryBreakdownRow}
        open={Boolean(secondaryBreakdownRow)}
        onClose={() => setSecondaryBreakdownRow(null)}
      />
      <ClosedTransactionsDialog
        row={closedTransactionsRow}
        open={Boolean(closedTransactionsRow)}
        onClose={() => setClosedTransactionsRow(null)}
      />
      <NewsDialog row={newsRow} open={Boolean(newsRow)} onClose={() => setNewsRow(null)} />
    </Box>
  );
};

export default DailyNav;
